import React, { useEffect } from 'react';
import { Card, CardActions, CardContent, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { convertMsToTimeFriendly } from '~/utils/dateTime';
import useSystemState from '~/features/System/stores/SystemState';
import Close from '@mui/icons-material/Close';
import { useLang } from '~/hooks/useLang';
import useDispatchState from '~/features/Dispatch/stores/DispatchState';
import { useHookstate } from '@hookstate/core';
import useMapState from '~/features/Map/stores/MapState';
import dayjs from 'dayjs';
import type IDispatch from '~/features/Dispatch/interfaces/IDispatch';

const elapsedTime = (initialDateTime?: string): string => {
  if (initialDateTime) {
    const dataInicial = new Date(initialDateTime)
    const dataAtual = new Date();
    const tempoDecorrido = dataAtual.getTime() - dataInicial.getTime();
    return convertMsToTimeFriendly(tempoDecorrido);
  }
  else
    return 'N/D';
}

// eslint-disable-next-line max-lines-per-function
export default function MarkerPopupContentVehiclePassage({ id, code, date, plate, currentDate, closeThisPopUp, brand, circunstance, color, model, typeVehicle, status }) {
  const { getStatusColors } = useSystemState();
  const { syncDispatchFromId, dispatch, dispatchStatusActions, loadingDispatchForm, getDispatchByCode } = useDispatchState();
  const { canClosePopup } = dispatchStatusActions();
  const { translate } = useLang();
  const { markers } = useMapState();
  const isAwaiting = useHookstate(false)
  const statusColors = getStatusColors(Number(dispatch()?.status?.id.get()));
  const dispatchVehiclePassage = useHookstate<IDispatch | null>(null);
  const isLoading = useHookstate(false);

  useEffect(() => {
    canClosePopup.get() && closeThisPopUp()
    isLoading.set(true)
    getDispatchByCode(code).promise.then((response) => {
      if (response) {
        dispatchVehiclePassage.set(response[0]);
      }
    }).finally(() => {
      isLoading.set(false);
    })
  }, [canClosePopup.get()])

  const handleSyncDispatch = async () => {
    isAwaiting.set(true);
    loadingDispatchForm.set(true)
    syncDispatchFromId(dispatchVehiclePassage.get()?.id)
      .finally(() => {
        isAwaiting.set(false);
        loadingDispatchForm.set(false)
        closeThisPopUp()
      });
  }

  return (
    <Card sx={{ backgroundColor: 'transparent', backgroundImage: 'none', boxShadow: 'none' }}>
      <CardContent sx={{ padding: '1em 0 1em 0' }}>
        <Stack direction='row' alignItems='center'>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            {plate &&
              <Tooltip title={translate('Plate')} placement='left'>
                <Stack direction='row' alignItems='center'>
                  <Typography variant='subtitle1' sx={{ textAlign: 'left' }}>
                    <b>
                      {translate('Plate')}
                    </b>
                    : {plate}
                  </Typography>
                </Stack>
              </Tooltip>
            }
            <div style={{ display: 'flex', gap: 2 }}>
              <IconButton size='small' onClick={() => closeThisPopUp()}>
                <Close fontSize='small' />
              </IconButton>
            </div>
          </div>
        </Stack>

        {typeVehicle &&
          <Tooltip title={translate('VehicleType:name')} placement='left'>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Typography variant='subtitle1' sx={{ textAlign: 'left' }}>
                <b>
                  {translate('VehicleType:name')}
                </b>
                : {typeVehicle}
              </Typography>
            </div>
          </Tooltip>
        }
        {circunstance &&
          <Tooltip title={translate('Circunstance')} placement='left'>
            <Stack direction='row' alignItems='center'>
              <Typography variant='subtitle1' sx={{ textAlign: 'left' }}>
                <b>
                  {translate('Circunstance')}
                </b>
                : {circunstance}
              </Typography>
            </Stack>
          </Tooltip>
        }
        {brand &&
          <Tooltip title={translate('Brand')} placement='left'>
            <Stack direction='row' alignItems='center'>
              <Typography variant='subtitle1' sx={{ textAlign: 'left' }}>
                <b>
                  {translate('Brand')}
                </b>
                : {brand}
              </Typography>
            </Stack>
          </Tooltip>
        }
        {model &&
          <Tooltip title={translate('Model')} placement='left'>
            <Stack direction='row' alignItems='center'>
              <Typography variant='subtitle1' sx={{ textAlign: 'left' }}>
                <b>
                  {translate('Model')}
                </b>
                : {model}
              </Typography>
            </Stack>
          </Tooltip>
        }
        {color &&
          <Tooltip title={translate('Color')} placement='left'>
            <Stack direction='row' alignItems='center'>
              <Typography variant='subtitle1' sx={{ textAlign: 'left' }}>
                <b>
                  {translate('Color')}
                </b>
                : {color}
              </Typography>
            </Stack>
          </Tooltip>
        }
        {currentDate &&
          <Tooltip title={translate('Last update')} placement='left'>
            <Stack direction='row' alignItems='center' justifyContent={'space-between'}>
              <Typography variant='subtitle1'
                sx={{ margin: '.4em 0' }}
              >
                <b>
                  {translate('Date')}
                </b>
                : {dayjs(currentDate).isValid() ? dayjs(currentDate).format('DD/MM/YYYY HH:mm') : ''}
              </Typography>
              <Chip variant={'outlined'} label={elapsedTime((currentDate ? currentDate.toString() : ''))} />
            </Stack>
          </Tooltip>
        }
        <CardActions>
          <Chip
            onClick={handleSyncDispatch}
            sx={{
              width: '100%',
              backgroundColor: status ? getStatusColors(status ?? null).main : null,
              color: status ? getStatusColors(status ?? null).contrastText : null,
              '&:hover': {
                backgroundColor: status ? getStatusColors(status ?? null).dark : null,
                color: status ? getStatusColors(status ?? null).contrastText : null,
              },
            }}
            label={(status != 10 && status != 11 && status != 12) ? (isAwaiting.get() || isLoading.get() ? <CircularProgress size={22} /> : code) : translate('Without Dispatch')}
          />
        </CardActions>

      </CardContent>
    </Card >
  )
}